import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AxiosService from "../../services/AxiosHelper";
import { toast } from "react-toastify";

const baseURL = process.env.REACT_APP_BASEURL;

// --- Thunks ---

export const onboardBusiness = createAsyncThunk(
    'transaction/onboard',
    async (businessId, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/transactions/onboard/${businessId}`,
            });
            return response.data.data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || "Failed to start onboarding";
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const authorizePayment = createAsyncThunk(
    'transaction/authorize',
    async (paymentData, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/transactions/authorize`,
                data: paymentData,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Authorization failed");
        }
    }
);

export const capturePayment = createAsyncThunk(
    'transaction/capture',
    async (appointmentId, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/transactions/capture/${appointmentId}`,
            });
            toast.success("Payment captured successfully!");
            return response.data.data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || "Failed to capture payment";
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const createPaymentLink = createAsyncThunk(
    'transaction/createPaymentLink',
    async (paymentData, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/transactions/create-payment-link`,
                data: paymentData
            });
            return response.data.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to generate link";
            toast.error(msg);
            return rejectWithValue(msg);
        }
    }
);

export const fetchExistingPaymentLink = createAsyncThunk(
    'transaction/fetchExistingLink',
    async (transactionId, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/transactions/existing-link/${transactionId}`,
            });
            return response.data.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Link has expired or is invalid";
            toast.error(msg);
            return rejectWithValue(msg);
        }
    }
);

export const fetchBusinessTransactions = createAsyncThunk(
    'transaction/fetchList',
    async ({ businessId, filters = {} }, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            if (filters.page) queryParams.append('page', filters.page);
            if (filters.limit) queryParams.append('limit', filters.limit);
            if (filters.status) queryParams.append('status', filters.status);
            if (filters.timeFilter) queryParams.append('timeFilter', filters.timeFilter);
            if (filters.customDate) queryParams.append('customDate', filters.customDate);

            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/transactions/business/${businessId}?${queryParams.toString()}`,
            });

            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to fetch history");
        }
    }
);

export const fetchTransactionDetail = createAsyncThunk(
    'transaction/fetchTransactionDetail',
    async (id, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/transactions/${id}`,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to fetch detail");
        }
    }
);

export const syncStripeStatus = createAsyncThunk(
    'transaction/syncStripeStatus',
    async (businessId, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/transactions/sync-stripe/${businessId}`,
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Sync failed");
        }
    }
);

export const syncPendingTransactions = createAsyncThunk(
    'transaction/syncPendingTransactions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/transactions/sync-pending`,
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Pending sync failed");
        }
    }
);

export const fetchCustomerTransactions = createAsyncThunk(
    'transaction/fetchCustomerTransactions',
    async ({ customerId, businessId }, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/transactions/customer/${customerId}?businessId=${businessId}`,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to fetch transactions");
        }
    }
);

export const refundTransaction = createAsyncThunk(
    'transaction/refund',
    async ({ transactionId, amount, reason }, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: `${baseURL}/transactions/refund/${transactionId}`,
                data: { amount, reason },
            });
            toast.success("Refund processed successfully");
            return response.data.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to process refund";
            toast.error(msg);
            return rejectWithValue(msg);
        }
    }
);

// --- Slice ---

const transactionSlice = createSlice({
    name: 'transaction',
    initialState: {
        transactions: [],
        selectedTransaction: null,
        onboardingUrl: null,
        paymentLinkUrl: null,
        paymentLinkStatus: null,
        lastCreatedTransactionId: null,
        pagination: null,
        loading: false,
        error: null,
        stripeStatus: null
    },
    reducers: {
        clearTransactionError: (state) => { state.error = null; },
        resetPaymentLinks: (state) => {
            state.paymentLinkUrl = null;
            state.paymentLinkStatus = null;
            state.onboardingUrl = null;
            state.lastCreatedTransactionId = null;
            state.loading = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBusinessTransactions.fulfilled, (state, action) => {
                state.loading = false;
                state.transactions = action.payload.transactions || [];
                state.pagination = action.payload.pagination || null;
            })
            .addCase(fetchCustomerTransactions.fulfilled, (state, action) => {
                state.loading = false;
                state.transactions = action.payload || [];
                state.pagination = null;
            })
            .addCase(onboardBusiness.fulfilled, (state, action) => {
                state.loading = false;
                state.onboardingUrl = action.payload.url;
            })
            .addCase(syncStripeStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.stripeStatus = action.payload.stripeStatus;
            })
            .addCase(createPaymentLink.fulfilled, (state, action) => {
                state.loading = false;
                state.paymentLinkUrl = action.payload.url;
                state.lastCreatedTransactionId = action.payload.transactionId;
            })
            .addCase(fetchExistingPaymentLink.fulfilled, (state, action) => {
                state.loading = false;
                state.paymentLinkUrl = action.payload.url;
                state.paymentLinkStatus = action.payload.status;
            })
            .addCase(capturePayment.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.transactions.findIndex(t => t._id === action.payload._id);
                if (index !== -1) {
                    state.transactions[index] = action.payload;
                }
            })
            .addCase(fetchTransactionDetail.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedTransaction = action.payload;
            });

        // General Matchers
        builder.addMatcher(
            (action) => action.type.startsWith("transaction/") && action.type.endsWith("/pending"),
            (state) => {
                state.loading = true;
                state.error = null;
            }
        );
        builder.addMatcher(
            (action) => action.type.startsWith("transaction/") && (action.type.endsWith("/fulfilled") || action.type.endsWith("/rejected")),
            (state, action) => {
                state.loading = false;
                if (action.type.endsWith("/rejected")) {
                    state.error = action.payload;
                }
            }
        );
    }
});

export const { clearTransactionError, resetPaymentLinks } = transactionSlice.actions;
export default transactionSlice.reducer;
